<template>
  <div class="message-templates">
    <v-row
      v-if="loading"
      justify="center"
      align="center"
      style="min-height:300px"
    >
      <v-progress-circular indeterminate color="primary" />
    </v-row>

    <template v-else>
      <v-row no-gutters style="min-height: 60vh">
        <!-- Sidebar: Categorías -->
        <v-col cols="12" sm="3" class="categories-panel pr-3">
          <div class="d-flex align-center justify-space-between mb-3">
            <span class="font-weight-bold text-subtitle-1">Categorías</span>
            <v-btn
              v-if="canManage"
              icon
              small
              color="primary"
              @click="openCategoryDialog()"
            >
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>

          <v-list dense class="pa-0">
            <v-list-item
              :class="[
                'category-item rounded',
                selectedCategoryId === null ? 'active-category' : ''
              ]"
              @click="selectedCategoryId = null"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Todas</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action-text class="text-caption grey--text">
                {{ templates.length }}
              </v-list-item-action-text>
            </v-list-item>

            <v-list-item
              v-for="cat in categories"
              :key="cat._id"
              :class="[
                'category-item rounded',
                selectedCategoryId === cat._id ? 'active-category' : ''
              ]"
              @click="selectedCategoryId = cat._id"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{
                  cat.name
                }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="canManage" class="category-actions">
                <div class="d-flex">
                  <v-btn icon x-small @click.stop="openCategoryDialog(cat)">
                    <v-icon x-small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canDelete"
                    icon
                    x-small
                    color="error"
                    @click.stop="confirmDeleteCategory(cat)"
                  >
                    <v-icon x-small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>

        <v-divider vertical />

        <!-- Templates panel -->
        <v-col cols="12" sm="9" class="pl-4">
          <div class="d-flex align-center justify-space-between mb-4">
            <span class="font-weight-bold text-subtitle-1">
              {{
                selectedCategoryId
                  ? selectedCategoryName
                  : "Todas las plantillas"
              }}
              <v-chip x-small class="ml-2" outlined>{{
                filteredTemplates.length
              }}</v-chip>
            </span>
            <v-btn
              v-if="canManage"
              small
              color="primary"
              depressed
              @click="openTemplateDialog()"
            >
              <v-icon left small>mdi-plus</v-icon>
              Nueva plantilla
            </v-btn>
          </div>

          <v-row v-if="filteredTemplates.length === 0">
            <v-col cols="12" class="text-center grey--text py-10">
              <v-icon large color="grey lighten-1">mdi-text-box-outline</v-icon>
              <div class="mt-2 text-body-2">
                Sin plantillas en esta categoría
              </div>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col
              v-for="tpl in filteredTemplates"
              :key="tpl._id"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card outlined class="template-card" height="100%">
                <v-card-text class="pb-1 pt-3 px-4">
                  <div class="d-flex align-start mb-1">
                    <span
                      class="flex-grow-1 text-body-1 font-weight-bold template-title"
                      >{{ tpl.title }}</span
                    >
                    <v-icon
                      v-if="tpl.isHighlighted"
                      small
                      color="amber darken-2"
                      class="ml-1 mt-1"
                    >
                      mdi-star
                    </v-icon>
                  </div>
                  <v-chip x-small outlined class="mb-2">
                    {{
                      tpl.categoryId && tpl.categoryId.name
                        ? tpl.categoryId.name
                        : "Sin categoría"
                    }}
                  </v-chip>
                </v-card-text>

                <v-card-text
                  class="template-content text-body-2 grey--text text--darken-1 pt-0 px-4"
                >
                  {{ tpl.content | truncate(100) }}
                </v-card-text>

                <v-card-actions class="pt-0">
                  <v-spacer />
                  <v-btn
                    v-if="canManage"
                    icon
                    small
                    @click="openTemplateDialog(tpl)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canDelete"
                    icon
                    small
                    color="error"
                    @click="confirmDeleteTemplate(tpl)"
                  >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>

    <!-- Dialog: Category -->
    <v-dialog
      v-model="categoryDialog"
      max-width="420"
      @click:outside="closeCategoryDialog"
    >
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ editingCategory ? "Editar categoría" : "Nueva categoría" }}
        </v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            v-model="categoryForm.name"
            label="Nombre"
            outlined
            dense
            :rules="[v => !!v || 'El nombre es requerido']"
            @keyup.enter="saveCategory"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCategoryDialog">Cancelar</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="savingCategory"
            @click="saveCategory"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Template -->
    <v-dialog
      v-model="templateDialog"
      max-width="680"
      @click:outside="closeTemplateDialog"
    >
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ editingTemplate ? "Editar plantilla" : "Nueva plantilla" }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="templateForm.title"
                label="Título"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="templateForm.categoryId"
                :items="categories"
                item-text="name"
                item-value="_id"
                label="Categoría"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-1">
                <label class="text-caption grey--text text--darken-2"
                  >Contenido</label
                >
                <v-btn
                  x-small
                  text
                  color="primary"
                  @click="variablePickerOpen = !variablePickerOpen"
                >
                  <v-icon x-small left>mdi-code-braces</v-icon>
                  Variables
                </v-btn>
              </div>

              <!-- Variable picker -->
              <v-expand-transition>
                <v-card
                  v-if="variablePickerOpen"
                  outlined
                  class="mb-2 pa-2"
                  max-height="180"
                  style="overflow-y: auto"
                >
                  <div v-if="loadingVariables" class="text-center py-2">
                    <v-progress-circular
                      indeterminate
                      size="20"
                      color="primary"
                    />
                  </div>
                  <div v-else>
                    <div
                      v-for="group in variables"
                      :key="group.name"
                      class="mb-2"
                    >
                      <div
                        class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                      >
                        {{ group.label }}
                      </div>
                      <v-chip
                        v-for="field in group.fields"
                        :key="field.alias"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                        style="cursor: pointer"
                        @click="insertVariable(field.alias)"
                      >
                        {{ field.label }}
                      </v-chip>
                    </div>
                  </div>
                </v-card>
              </v-expand-transition>

              <v-textarea
                ref="contentArea"
                v-model="templateForm.content"
                outlined
                dense
                rows="5"
                :placeholder="contentPlaceholder"
                no-resize
              />
            </v-col>
            <v-col cols="12">
              <v-checkbox
                v-model="templateForm.isHighlighted"
                label="Plantilla destacada"
                dense
                hide-details
                class="mt-0"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeTemplateDialog">Cancelar</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="savingTemplate"
            @click="saveTemplate"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm delete dialog -->
    <v-dialog v-model="deleteDialog" max-width="380">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Eliminar {{ deleteTarget === "category" ? "categoría" : "plantilla" }}
        </v-card-title>
        <v-card-text>
          ¿Estás seguro que deseas eliminar
          <strong>{{ deleteItemName }}</strong
          >? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn
            color="error"
            depressed
            :loading="deleting"
            @click="executeDelete"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      top
      right
      timeout="3000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getVariables
} from "@/api/template-message-api.js";

const MANAGE_ROLES = ["root", "admin", "coordinator"];
const DELETE_ROLES = ["root", "admin"];

export default {
  name: "MessageTemplates",
  filters: {
    truncate(val, len) {
      if (!val) return "";
      return val.length > len ? val.substring(0, len) + "…" : val;
    }
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      categories: [],
      templates: [],
      variables: [],
      selectedCategoryId: null,
      // Category dialog
      categoryDialog: false,
      editingCategory: null,
      categoryForm: { name: "" },
      savingCategory: false,
      // Template dialog
      templateDialog: false,
      editingTemplate: null,
      templateForm: {
        title: "",
        content: "",
        categoryId: null,
        isHighlighted: false
      },
      savingTemplate: false,
      variablePickerOpen: false,
      loadingVariables: false,
      // Delete
      deleteDialog: false,
      deleteTarget: null,
      deleteItem: null,
      deleting: false,
      // Snackbar
      snackbar: { show: false, message: "", color: "success" }
    };
  },
  computed: {
    userRole() {
      return this.$store.state.role || "";
    },
    canManage() {
      return MANAGE_ROLES.includes(this.userRole);
    },
    canDelete() {
      return DELETE_ROLES.includes(this.userRole);
    },
    filteredTemplates() {
      if (!this.selectedCategoryId) return this.templates;
      return this.templates.filter(
        t => t.categoryId && t.categoryId._id === this.selectedCategoryId
      );
    },
    selectedCategoryName() {
      const cat = this.categories.find(c => c._id === this.selectedCategoryId);
      return cat ? cat.name : "";
    },
    deleteItemName() {
      return this.deleteItem
        ? this.deleteItem.name || this.deleteItem.title
        : "";
    },
    contentPlaceholder() {
      return "Escribe el contenido de la plantilla. Usa {{nombre_lead}} para insertar variables.";
    }
  },
  watch: {
    visible(val) {
      if (val) this.loadData();
    }
  },
  mounted() {
    if (this.visible) this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [catRes, tplRes] = await Promise.all([
          getCategories(),
          getTemplates()
        ]);
        this.categories = Array.isArray(catRes.data) ? catRes.data : [];
        this.templates = Array.isArray(tplRes.data) ? tplRes.data : [];
      } finally {
        this.loading = false;
      }
    },
    async loadVariables() {
      if (this.variables.length) return;
      this.loadingVariables = true;
      try {
        const res = await getVariables();
        this.variables = Array.isArray(res.data) ? res.data : [];
      } finally {
        this.loadingVariables = false;
      }
    },
    // Category
    openCategoryDialog(cat = null) {
      this.editingCategory = cat;
      this.categoryForm.name = cat ? cat.name : "";
      this.categoryDialog = true;
    },
    closeCategoryDialog() {
      this.categoryDialog = false;
      this.editingCategory = null;
      this.categoryForm.name = "";
    },
    async saveCategory() {
      if (!this.categoryForm.name.trim()) return;
      this.savingCategory = true;
      try {
        if (this.editingCategory) {
          const res = await updateCategory(
            this.editingCategory._id,
            this.categoryForm.name.trim()
          );
          const updated = res.data;
          const idx = this.categories.findIndex(
            c => c._id === this.editingCategory._id
          );
          if (idx >= 0) this.$set(this.categories, idx, updated);
        } else {
          const res = await createCategory(this.categoryForm.name.trim());
          this.categories.push(res.data);
        }
        this.showSnackbar("Categoría guardada correctamente");
        this.closeCategoryDialog();
      } catch {
        this.showSnackbar("Error al guardar la categoría", "error");
      } finally {
        this.savingCategory = false;
      }
    },
    confirmDeleteCategory(cat) {
      this.deleteTarget = "category";
      this.deleteItem = cat;
      this.deleteDialog = true;
    },
    // Template
    openTemplateDialog(tpl = null) {
      this.editingTemplate = tpl;
      this.templateForm = {
        title: tpl ? tpl.title : "",
        content: tpl ? tpl.content : "",
        categoryId:
          tpl && tpl.categoryId
            ? tpl.categoryId._id
            : this.selectedCategoryId || null,
        isHighlighted: tpl ? !!tpl.isHighlighted : false
      };
      this.variablePickerOpen = false;
      this.templateDialog = true;
      this.loadVariables();
    },
    closeTemplateDialog() {
      this.templateDialog = false;
      this.editingTemplate = null;
      this.variablePickerOpen = false;
    },
    async saveTemplate() {
      if (!this.templateForm.title.trim()) return;
      this.savingTemplate = true;
      try {
        const body = {
          title: this.templateForm.title.trim(),
          content: this.templateForm.content,
          isHighlighted: this.templateForm.isHighlighted
        };
        if (this.templateForm.categoryId)
          body.categoryId = this.templateForm.categoryId;

        if (this.editingTemplate) {
          const res = await updateTemplate(this.editingTemplate._id, body);
          const updated = this.populateCategoryId(res.data);
          const idx = this.templates.findIndex(
            t => t._id === this.editingTemplate._id
          );
          if (idx >= 0) this.$set(this.templates, idx, updated);
        } else {
          const res = await createTemplate(body);
          this.templates.push(this.populateCategoryId(res.data));
        }
        this.showSnackbar("Plantilla guardada correctamente");
        this.closeTemplateDialog();
      } catch {
        this.showSnackbar("Error al guardar la plantilla", "error");
      } finally {
        this.savingTemplate = false;
      }
    },
    populateCategoryId(tpl) {
      if (!tpl || !tpl.categoryId || typeof tpl.categoryId === "object")
        return tpl;
      const cat = this.categories.find(c => c._id === tpl.categoryId);
      return { ...tpl, categoryId: cat || null };
    },
    confirmDeleteTemplate(tpl) {
      this.deleteTarget = "template";
      this.deleteItem = tpl;
      this.deleteDialog = true;
    },
    async executeDelete() {
      this.deleting = true;
      try {
        if (this.deleteTarget === "category") {
          await deleteCategory(this.deleteItem._id);
          this.categories = this.categories.filter(
            c => c._id !== this.deleteItem._id
          );
          if (this.selectedCategoryId === this.deleteItem._id)
            this.selectedCategoryId = null;
          this.showSnackbar("Categoría eliminada correctamente");
        } else {
          await deleteTemplate(this.deleteItem._id);
          this.templates = this.templates.filter(
            t => t._id !== this.deleteItem._id
          );
          this.showSnackbar("Plantilla eliminada correctamente");
        }
        this.deleteDialog = false;
        this.deleteItem = null;
      } catch {
        this.showSnackbar("Error al eliminar", "error");
      } finally {
        this.deleting = false;
      }
    },
    insertVariable(alias) {
      const tag = `{{${alias}}}`;
      const textarea = this.$refs.contentArea.$el.querySelector("textarea");
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const current = this.templateForm.content || "";
        this.templateForm.content =
          current.substring(0, start) + tag + current.substring(end);
        this.$nextTick(() => {
          const pos = start + tag.length;
          textarea.setSelectionRange(pos, pos);
          textarea.focus();
        });
      } else {
        this.templateForm.content = (this.templateForm.content || "") + tag;
      }
    },
    showSnackbar(message, color = "success") {
      this.snackbar = { show: true, message, color };
    }
  }
};
</script>

<style scoped>
.categories-panel {
  border-right: 1px solid #e0e0e0;
}
.category-item {
  min-height: 36px;
  cursor: pointer;
  transition: background 0.15s;
}
.category-item:hover {
  background: #f5f5f5;
}
.active-category {
  background: #e3f2fd !important;
  color: #1976d2;
}
.active-category .v-list-item__title {
  color: #1976d2;
  font-weight: 600;
}
.category-actions {
  min-width: auto;
}
.template-card {
  transition: box-shadow 0.2s;
}
.template-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
.template-title {
  word-break: break-word;
  line-height: 1.3;
}
.template-content {
  word-break: break-word;
  white-space: pre-wrap;
}
</style>
